import { TOOLS } from '@shared/protocol.js';
import { buildShapePath, isOpenShape } from '@shared/shapes.js';
import type {
  Camera, LiveStroke, Op, ShapeOp, FillOp, StrokeStyle, Viewport, Bounds,
} from './types';
import { visibleRect, worldToScreen } from './camera';

/** Decoded images, keyed by data URL, so a redraw never re-decodes. */
const imageCache = new Map<string, HTMLImageElement>();
let onImageLoad: (() => void) | null = null;

export function setImageLoadListener(fn: (() => void) | null) {
  onImageLoad = fn;
}

function getImage(src: string): HTMLImageElement | null {
  const cached = imageCache.get(src);
  if (cached) return cached.complete && cached.naturalWidth > 0 ? cached : null;
  const img = new Image();
  img.onload = () => onImageLoad?.();
  img.src = src;
  imageCache.set(src, img);
  return null;
}

/** Apply the camera so all subsequent drawing can use world coordinates. */
export function applyCamera(ctx: CanvasRenderingContext2D, cam: Camera, vp: Viewport) {
  ctx.setTransform(
    cam.zoom * vp.dpr, 0,
    0, cam.zoom * vp.dpr,
    (vp.width / 2 - cam.x * cam.zoom) * vp.dpr,
    (vp.height / 2 - cam.y * cam.zoom) * vp.dpr,
  );
}

export function resetTransform(ctx: CanvasRenderingContext2D, vp: Viewport) {
  ctx.setTransform(vp.dpr, 0, 0, vp.dpr, 0, 0);
}

// --- background -----------------------------------------------------------

const GRID_STEP_CHOICES = [1, 2, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000];

/** Pick a grid spacing that stays visually comfortable at any zoom. */
function gridStep(zoom: number) {
  const target = 28 / zoom; // aim for ~28 device px between lines
  for (const step of GRID_STEP_CHOICES) if (step >= target) return step;
  return GRID_STEP_CHOICES[GRID_STEP_CHOICES.length - 1];
}

export function drawBackground(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  vp: Viewport,
  style: 'plain' | 'dots' | 'grid' | 'lines',
  dark: boolean,
) {
  if (style === 'plain') return;
  const rect = visibleRect(cam, vp);
  const step = gridStep(cam.zoom);
  const ink = dark ? 'rgba(255,255,255,0.13)' : 'rgba(17,20,24,0.13)';

  ctx.save();
  applyCamera(ctx, cam, vp);
  ctx.strokeStyle = ink;
  ctx.fillStyle = ink;
  ctx.lineWidth = Math.max(0.4, 1 / cam.zoom);

  const x0 = Math.floor(rect.minX / step) * step;
  const y0 = Math.floor(rect.minY / step) * step;

  if (style === 'dots') {
    const r = Math.max(0.5, 1.1 / cam.zoom);
    for (let x = x0; x <= rect.maxX; x += step) {
      for (let y = y0; y <= rect.maxY; y += step) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  } else {
    ctx.beginPath();
    if (style !== 'lines') {
      for (let x = x0; x <= rect.maxX; x += step) {
        ctx.moveTo(x, rect.minY);
        ctx.lineTo(x, rect.maxY);
      }
    }
    for (let y = y0; y <= rect.maxY; y += step) {
      ctx.moveTo(rect.minX, y);
      ctx.lineTo(rect.maxX, y);
    }
    ctx.stroke();
  }
  ctx.restore();
}

// --- strokes --------------------------------------------------------------

function toolComposite(tool: string): GlobalCompositeOperation {
  if (tool === TOOLS.ERASER) return 'destination-out';
  if (tool === TOOLS.HIGHLIGHTER) return 'multiply';
  return 'source-over';
}

function toolWidth(style: StrokeStyle) {
  if (style.tool === TOOLS.HIGHLIGHTER) return style.size * 2.6;
  if (style.tool === TOOLS.ERASER) return style.size * 2;
  // A marker lays down a noticeably fatter, flatter line than a pen.
  if (style.tool === TOOLS.MARKER) return style.size * 1.8;
  return style.size;
}

function toolAlpha(style: StrokeStyle) {
  if (style.tool === TOOLS.HIGHLIGHTER) return Math.min(1, style.opacity * 0.42);
  return style.opacity;
}

/**
 * Trace a smooth path through the points using quadratic segments between
 * midpoints — the classic trick that turns jittery pointer samples into a
 * curve without needing a full spline fit.
 */
function tracePath(ctx: CanvasRenderingContext2D, pts: number[], smooth: boolean, from = 0, to = -1) {
  const end = to < 0 ? pts.length / 3 : to;
  if (end - from < 1) return;
  ctx.moveTo(pts[from * 3], pts[from * 3 + 1]);
  if (end - from === 1) {
    ctx.lineTo(pts[from * 3], pts[from * 3 + 1]);
    return;
  }
  if (!smooth) {
    for (let i = from + 1; i < end; i += 1) ctx.lineTo(pts[i * 3], pts[i * 3 + 1]);
    return;
  }
  for (let i = from + 1; i < end - 1; i += 1) {
    const cx = pts[i * 3];
    const cy = pts[i * 3 + 1];
    const mx = (cx + pts[(i + 1) * 3]) / 2;
    const my = (cy + pts[(i + 1) * 3 + 1]) / 2;
    ctx.quadraticCurveTo(cx, cy, mx, my);
  }
  ctx.lineTo(pts[(end - 1) * 3], pts[(end - 1) * 3 + 1]);
}

/** True when the stroke's pressure values actually vary enough to matter. */
function hasPressureVariation(pts: number[]) {
  if (pts.length < 9) return false;
  let min = 1;
  let max = 0;
  for (let i = 2; i < pts.length; i += 3) {
    if (pts[i] < min) min = pts[i];
    if (pts[i] > max) max = pts[i];
  }
  return max - min > 0.08;
}

export function drawStroke(
  ctx: CanvasRenderingContext2D,
  style: StrokeStyle,
  points: number[],
  usePressure: boolean,
) {
  if (points.length < 3) return;
  const width = toolWidth(style);

  ctx.save();
  ctx.globalCompositeOperation = toolComposite(style.tool);
  ctx.globalAlpha = toolAlpha(style);
  ctx.strokeStyle = style.color;
  ctx.lineJoin = 'round';
  ctx.lineCap = style.tool === TOOLS.HIGHLIGHTER ? 'butt' : 'round';

  if (style.tool === TOOLS.NEON) {
    // The halo is built from layered translucent passes rather than
    // `shadowBlur`, which is measured in device pixels and so would grow and
    // shrink wrongly as the camera zooms. Stacked strokes live in world space
    // like everything else, and are cheaper besides.
    const halo: Array<[number, number]> = [[2.8, 0.09], [2.0, 0.13], [1.4, 0.2]];
    for (const [scale, alpha] of halo) {
      ctx.globalAlpha = style.opacity * alpha;
      ctx.lineWidth = width * scale;
      ctx.beginPath();
      tracePath(ctx, points, style.smooth);
      ctx.stroke();
    }

    ctx.globalAlpha = style.opacity;
    ctx.lineWidth = width;
    ctx.beginPath();
    tracePath(ctx, points, style.smooth);
    ctx.stroke();

    // A thin hot centre, the way a lit tube looks. Any wider and the stroke
    // reads as a hollow outline instead of a glow.
    ctx.strokeStyle = '#ffffff';
    ctx.globalAlpha = style.opacity * 0.5;
    ctx.lineWidth = Math.max(0.4, width * 0.2);
    ctx.beginPath();
    tracePath(ctx, points, style.smooth);
    ctx.stroke();
    ctx.restore();
    return;
  }

  if (style.tool === TOOLS.MARKER) {
    // Felt tip: constant width, and a faint wider pass underneath so the edge
    // reads as ink bleeding into the paper rather than a hard vector line.
    ctx.globalAlpha = toolAlpha(style) * 0.22;
    ctx.lineWidth = width * 1.3;
    ctx.beginPath();
    tracePath(ctx, points, style.smooth);
    ctx.stroke();

    ctx.globalAlpha = toolAlpha(style);
    ctx.lineWidth = width;
    ctx.beginPath();
    tracePath(ctx, points, style.smooth);
    ctx.stroke();

    if (points.length === 3) {
      ctx.fillStyle = style.color;
      ctx.beginPath();
      ctx.arc(points[0], points[1], width / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    return;
  }

  // Only the pen tapers with pressure; that taper is what makes it a pen.
  const pressure = usePressure && style.tool === TOOLS.PEN && hasPressureVariation(points);

  if (!pressure) {
    // Single path: by far the cheapest and the common case.
    ctx.lineWidth = width;
    ctx.beginPath();
    tracePath(ctx, points, style.smooth);
    ctx.stroke();

    if (points.length === 3) {
      // A tap should still leave a dot.
      ctx.fillStyle = style.color;
      ctx.beginPath();
      ctx.arc(points[0], points[1], width / 2, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
    return;
  }

  // Pressure: stroke segment by segment, easing the width between samples.
  const n = points.length / 3;
  for (let i = 0; i + 1 < n; i += 1) {
    const p0 = points[i * 3 + 2];
    const p1 = points[(i + 1) * 3 + 2];
    // A wide range so a light stroke is visibly finer than a heavy one.
    const w = width * (0.18 + 0.82 * ((p0 + p1) / 2));
    ctx.lineWidth = Math.max(0.05, w);
    ctx.beginPath();
    const ax = points[i * 3];
    const ay = points[i * 3 + 1];
    const bx = points[(i + 1) * 3];
    const by = points[(i + 1) * 3 + 1];
    if (style.smooth && i > 0 && i + 2 < n) {
      ctx.moveTo((points[(i - 1) * 3] + ax) / 2, (points[(i - 1) * 3 + 1] + ay) / 2);
      ctx.quadraticCurveTo(ax, ay, (ax + bx) / 2, (ay + by) / 2);
    } else {
      ctx.moveTo(ax, ay);
      ctx.lineTo(bx, by);
    }
    ctx.stroke();
  }
  ctx.restore();
}

// --- other op kinds -------------------------------------------------------

function drawArrowHead(ctx: CanvasRenderingContext2D, x0: number, y0: number, x1: number, y1: number, size: number) {
  const angle = Math.atan2(y1 - y0, x1 - x0);
  const head = Math.max(size * 3, 6);
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x1 - head * Math.cos(angle - Math.PI / 7), y1 - head * Math.sin(angle - Math.PI / 7));
  ctx.moveTo(x1, y1);
  ctx.lineTo(x1 - head * Math.cos(angle + Math.PI / 7), y1 - head * Math.sin(angle + Math.PI / 7));
  ctx.stroke();
}

function drawShape(ctx: CanvasRenderingContext2D, op: ShapeOp) {
  ctx.save();
  ctx.globalAlpha = op.opacity;
  ctx.strokeStyle = op.color;
  ctx.lineWidth = op.size;
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  ctx.beginPath();
  buildShapePath(op.shape, op.x0, op.y0, op.x1 - op.x0, op.y1 - op.y0, ctx);

  if (op.fill && !isOpenShape(op.shape)) {
    ctx.fillStyle = op.fill;
    ctx.fill();
  }
  ctx.stroke();

  // Arrow heads need the stroke width, which the geometry does not know about.
  if (op.shape === 'arrow' || op.shape === 'double-arrow') {
    drawArrowHead(ctx, op.x0, op.y0, op.x1, op.y1, op.size);
    if (op.shape === 'double-arrow') drawArrowHead(ctx, op.x1, op.y1, op.x0, op.y0, op.size);
  }

  ctx.restore();
}

export function drawOp(ctx: CanvasRenderingContext2D, op: Op, usePressure: boolean) {
  switch (op.kind) {
    case 'stroke':
      drawStroke(ctx, op, op.points, usePressure);
      break;

    case 'shape':
      drawShape(ctx, op);
      break;

    case 'stamp':
      ctx.save();
      ctx.translate(op.x, op.y);
      if (op.rotation) ctx.rotate(op.rotation);
      ctx.font = `${op.size}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(op.char, 0, 0);
      ctx.restore();
      break;

    case 'text':
      ctx.save();
      ctx.fillStyle = op.color;
      ctx.font = `${op.size}px ${op.font === 'mono'
        ? 'ui-monospace, SFMono-Regular, Menlo, monospace'
        : 'system-ui, -apple-system, "Segoe UI", sans-serif'}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(op.text, op.x, op.y);
      ctx.restore();
      break;

    case 'image': {
      const img = getImage(op.src);
      if (!img) break;
      ctx.save();
      ctx.globalAlpha = op.opacity;
      ctx.drawImage(img, op.x, op.y, op.w, op.h);
      ctx.restore();
      break;
    }

    case 'fill':
      drawFill(ctx, op);
      break;

    default:
      break;
  }
}

/**
 * A flood fill. Every ring goes into one path and is filled even-odd, so a
 * region traced with holes in it — the background around a shape, say — comes
 * out with those holes intact.
 */
export function drawFill(ctx: CanvasRenderingContext2D, op: FillOp) {
  ctx.save();
  ctx.globalAlpha = op.opacity;
  ctx.fillStyle = op.color;
  ctx.beginPath();
  for (const ring of op.rings) {
    if (ring.length < 6) continue;
    ctx.moveTo(ring[0], ring[1]);
    for (let i = 2; i < ring.length; i += 2) ctx.lineTo(ring[i], ring[i + 1]);
    ctx.closePath();
  }
  ctx.fill('evenodd');
  ctx.restore();
}

/**
 * Paint committed ops plus any strokes still in flight, in one pass.
 *
 * Live strokes go through the same pipeline as committed ones so that an
 * in-progress eraser rubs out the artwork underneath it exactly as it will once
 * it lands — no surprise when the stroke commits.
 */
export function renderScene(
  ctx: CanvasRenderingContext2D,
  vp: Viewport,
  cam: Camera,
  ops: Op[],
  live: LiveStroke[],
  usePressure: boolean,
) {
  ctx.save();
  applyCamera(ctx, cam, vp);
  for (const op of ops) drawOp(ctx, op, usePressure);
  for (const stroke of live) drawStroke(ctx, stroke.style, stroke.points, usePressure);
  ctx.restore();
}

// --- overlay --------------------------------------------------------------

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}

/**
 * Outline of where a peer is looking, drawn in screen space so the label stays
 * legible however far out you zoom.
 */
export function drawPeerViewport(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  vp: Viewport,
  peerView: { cx: number; cy: number; zoom: number; w: number; h: number },
  label: string,
  color: string,
  dimmed: boolean,
) {
  const halfW = peerView.w / (2 * peerView.zoom);
  const halfH = peerView.h / (2 * peerView.zoom);
  const tl = worldToScreen(cam, vp, peerView.cx - halfW, peerView.cy - halfH);
  const br = worldToScreen(cam, vp, peerView.cx + halfW, peerView.cy + halfH);
  const w = br.x - tl.x;
  const h = br.y - tl.y;

  // When someone is looking at almost exactly what we are, a frame around the
  // whole screen is noise rather than information — unless we are deliberately
  // in the overview, where the point is to see every frame.
  const coversScreen = tl.x < 8 && tl.y < 8 && br.x > vp.width - 8 && br.y > vp.height - 8;
  if (coversScreen && dimmed) return;

  ctx.save();
  ctx.globalAlpha = dimmed ? 0.45 : 1;
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.setLineDash([7, 5]);
  roundRect(ctx, tl.x, tl.y, w, h, 10);
  ctx.stroke();
  ctx.setLineDash([]);

  // Only tint in the overview, and only lightly: several overlapping frames
  // stack up fast.
  if (!dimmed && !coversScreen) {
    ctx.globalAlpha = 0.06;
    ctx.fillStyle = color;
    ctx.fill();
  }

  // Label, pinned inside the frame so it never drifts off screen.
  ctx.globalAlpha = 1;
  ctx.font = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';
  const textW = ctx.measureText(label).width;
  const padX = 8;
  const boxW = textW + padX * 2;
  const boxH = 22;
  // If the frame starts above the top of the screen the label would slide up
  // under the toolbars, so drop it below them instead.
  const minY = tl.y < 0 ? 76 : 6;
  const bx = Math.min(Math.max(tl.x + 6, 6), Math.max(6, vp.width - boxW - 6));
  const by = Math.min(Math.max(tl.y + 6, minY), Math.max(minY, vp.height - boxH - 6));
  ctx.fillStyle = color;
  roundRect(ctx, bx, by, boxW, boxH, 11);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, bx + padX, by + boxH / 2 + 0.5);
  ctx.restore();
}

/** Another person's pointer, with a name tag. */
export function drawPeerCursor(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  vp: Viewport,
  cursor: { x: number; y: number; down: boolean },
  label: string,
  color: string,
  showName: boolean,
) {
  const p = worldToScreen(cam, vp, cursor.x, cursor.y);
  if (p.x < -80 || p.y < -80 || p.x > vp.width + 80 || p.y > vp.height + 80) return;

  ctx.save();
  ctx.translate(p.x, p.y);

  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(0, 15);
  ctx.lineTo(4.2, 11.4);
  ctx.lineTo(7, 17);
  ctx.lineTo(9.6, 15.8);
  ctx.lineTo(6.8, 10.3);
  ctx.lineTo(11.6, 9.8);
  ctx.closePath();
  ctx.fillStyle = color;
  ctx.strokeStyle = 'rgba(255,255,255,0.9)';
  ctx.lineWidth = 1.4;
  ctx.fill();
  ctx.stroke();

  if (cursor.down) {
    ctx.beginPath();
    ctx.arc(0, 0, 13, 0, Math.PI * 2);
    ctx.strokeStyle = color;
    ctx.globalAlpha = 0.5;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  if (showName) {
    ctx.font = '600 11px system-ui, -apple-system, "Segoe UI", sans-serif';
    const textW = ctx.measureText(label).width;
    ctx.fillStyle = color;
    roundRect(ctx, 13, 12, textW + 12, 19, 9.5);
    ctx.fill();
    ctx.fillStyle = '#fff';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, 19, 22);
  }
  ctx.restore();
}

/** Fading laser trail. `age` is 0..1. */
export function drawLaser(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  vp: Viewport,
  points: number[],
  color: string,
  age: number,
) {
  if (points.length < 6) return;
  ctx.save();
  applyCamera(ctx, cam, vp);
  ctx.globalAlpha = Math.max(0, 1 - age) * 0.85;
  ctx.strokeStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.lineWidth = 4 / cam.zoom;
  ctx.shadowColor = color;
  ctx.shadowBlur = 12 / cam.zoom;
  ctx.beginPath();
  tracePath(ctx, points, true);
  ctx.stroke();
  ctx.restore();
}

/** Dashed frame showing how far you can currently pan. */
export function drawPanFrontier(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  vp: Viewport,
  region: Bounds,
  dark: boolean,
) {
  const tl = worldToScreen(cam, vp, region.minX, region.minY);
  const br = worldToScreen(cam, vp, region.maxX, region.maxY);
  ctx.save();
  ctx.strokeStyle = dark ? 'rgba(255,255,255,0.22)' : 'rgba(17,20,24,0.18)';
  ctx.lineWidth = 2;
  ctx.setLineDash([12, 10]);
  ctx.strokeRect(tl.x, tl.y, br.x - tl.x, br.y - tl.y);
  ctx.restore();
}

/**
 * Selection frame and resize grips, drawn in screen space so the grips stay a
 * comfortable size to grab however far in or out you are zoomed.
 */
export function drawSelection(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  vp: Viewport,
  box: Bounds,
  handleRadius: number,
  dark: boolean,
) {
  const tl = worldToScreen(cam, vp, box.minX, box.minY);
  const br = worldToScreen(cam, vp, box.maxX, box.maxY);
  const x = Math.min(tl.x, br.x);
  const y = Math.min(tl.y, br.y);
  const w = Math.abs(br.x - tl.x);
  const h = Math.abs(br.y - tl.y);
  // Brand pink: it stands out against most things people draw.
  const accent = '#f7288e';

  ctx.save();

  // A dark halo under the frame keeps it visible on top of orange artwork.
  ctx.strokeStyle = dark ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.85)';
  ctx.lineWidth = 3.5;
  ctx.strokeRect(x, y, w, h);
  ctx.strokeStyle = accent;
  ctx.lineWidth = 1.5;
  ctx.strokeRect(x, y, w, h);

  const mx = x + w / 2;
  const my = y + h / 2;
  const grips: Array<[number, number]> = [
    [x, y], [mx, y], [x + w, y],
    [x + w, my], [x + w, y + h], [mx, y + h],
    [x, y + h], [x, my],
  ];

  for (const [gx, gy] of grips) {
    ctx.beginPath();
    ctx.arc(gx, gy, handleRadius, 0, Math.PI * 2);
    ctx.fillStyle = dark ? '#17181c' : '#ffffff';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = accent;
    ctx.stroke();
  }

  ctx.restore();
}

/** Live preview while dragging out a shape. */
export function drawShapePreview(
  ctx: CanvasRenderingContext2D,
  cam: Camera,
  vp: Viewport,
  op: Op,
) {
  ctx.save();
  applyCamera(ctx, cam, vp);
  ctx.globalAlpha = 0.85;
  drawOp(ctx, op, false);
  ctx.restore();
}
